'use client'

import { useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Widget } from '../Widget'
import type { ProcessedTheme, BrowserRow } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDateNumeric } from '../ProcessedEntry/DomainEntry/processedTypes'
import { ScreenshotCaptures } from './ScreenshotCaptures'
import { captureFromBrowserRow, sortCapturesNewestFirst } from './screenshotCaptures'

// An entry's browser captures (entry_browser_data rows the page already
// holds from /api/campaigns/ctt/entry-domains) in the shared ScreenshotCaptures
// widget, framed the way "Last obtained images" was: a Widget with the
// newest capture's date and a reload button, or bare (CTT's processed entry
// detail, which draws its own frame). Replaces LatestObtainedImagesPanel,
// which fetched its own list and one full-size screenshot per thumbnail in
// sight -- see ScreenshotCaptures for the loading policy.
export function EntryScreenshotsPanel({ rows, theme, getToken, bare = false, onReload, reloading = false, onShownLoaded }: {
  rows: BrowserRow[]
  theme: ProcessedTheme
  getToken: () => Promise<string>
  /** No Widget frame: a header line and the widget body only. */
  bare?: boolean
  /** Reload also re-fetches the rows (the page's own fetch); the shown
   * capture's images are re-fetched either way. */
  onReload?: () => void
  reloading?: boolean
  onShownLoaded?: (desktop: string | null, mobile: string | null) => void
}) {
  const c = watchClasses(theme)
  const captures = useMemo(() => sortCapturesNewestFirst(rows.map(captureFromBrowserRow)), [rows])
  const [reloadKey, setReloadKey] = useState(0)
  const reload = () => { setReloadKey((k) => k + 1); onReload?.() }
  const newest = captures[0]
  const body = <ScreenshotCaptures captures={captures} theme={theme} getToken={getToken} reloadKey={reloadKey} onShownLoaded={onShownLoaded} />
  const reloadButton = (
    <button type="button" onClick={reload} disabled={reloading} title="Reload images" aria-label="Reload images"
      className={`shrink-0 rounded p-1 transition-colors hover:text-signal disabled:opacity-50 ${c.faint}`}>
      <RefreshCw className={`h-3 w-3 ${reloading ? 'animate-spin' : ''}`} />
    </button>
  )

  if (bare) {
    return (
      <>
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>// Last obtained images</span>
          <div className="flex items-center gap-2">
            {newest && <span className={`font-mono text-[10px] ${c.faint}`}>{compactDateNumeric(newest.createdAt)}</span>}
            {reloadButton}
          </div>
        </div>
        {body}
      </>
    )
  }

  return (
    <Widget
      label={<>Screenshots{captures.length > 0 ? ` · ${captures.length}` : ''}</>}
      theme={theme}
      headerRight={
        <div className="flex items-center justify-end gap-2">
          {newest && <span className={`font-mono text-[10px] ${c.faint}`}>{compactDateNumeric(newest.createdAt)}</span>}
          {reloadButton}
        </div>
      }
    >
      {body}
    </Widget>
  )
}
